// SCSS Imports
import "./VideoPlayer.scss";

// React Imports
import React, { useEffect, useRef } from "react";

// VideoJS Imports
import videojs from "video.js";
import "video.js/dist/video-js.css";
import '@videojs/http-streaming';
import 'videojs-contrib-quality-levels';
import 'videojs-hls-quality-selector';

const VideoPlayer = ({ videoUrl, poster, captions }) => {
    const videoRef = useRef(null);
    const playerRef = useRef(null);
    const isIOS = videojs.browser.IS_IOS;

    useEffect(() => {
        // Only initialize or update if we have a videoUrl
        if (!videoUrl) return;

        const initPlayer = () => {
            try {
                // If player exists, just update the source
                if (playerRef.current) {
                    let videoType = videoUrl.endsWith(".m3u8") 
                        ? "application/x-mpegURL" 
                        : "video/mp4";

                    playerRef.current.src({ type: videoType, src: videoUrl });
                    
                    // Update poster if provided
                    if (poster) {
                        playerRef.current.poster(poster);
                    }

                    // Update captions
                    const tracks = playerRef.current.remoteTextTracks();
                    const trackElements = playerRef.current.remoteTextTrackEls();
                    
                    // Remove existing captions
                    for (let i = tracks.length - 1; i >= 0; i--) {
                        playerRef.current.removeRemoteTextTrack(tracks[i]);
                    }

                    // Add new captions if provided
                    if (captions) {
                        playerRef.current.addRemoteTextTrack({
                            src: captions,
                            label: "English",
                            kind: "captions",
                            srclang: "en-us",
                            default: true,
                        }, false);
                    }
                } else {
                    // Initialize new player if none exists
                    playerRef.current = videojs(videoRef.current, {
                        controls: true,
                        autoplay: true,
                        preload: "auto",
                        poster: poster,
                        nativeControlsForTouch: isIOS,
                        width: 1280,
                        playbackRates: [0.5, 1, 1.5, 2, 2.5],
                        fill: true,
                        fluid: true,
                        responsive: true,
                        aspectRatio: '16:9',
                        html5: {
                            vhs: {
                                overrideNative: !isIOS,
                                enableLowInitialPlaylist: true,
                            },
                            nativeVideoTracks: isIOS,
                            nativeAudioTracks: isIOS,
                            nativeTextTracks: false,
                        },
                        playsinline: true,
                    });

                    // Initialize quality selector plugin if not on iOS
                    if (!isIOS) {
                        playerRef.current.hlsQualitySelector({
                            displayCurrentQuality: true,
                        });
                    }

                    let videoType = videoUrl.endsWith(".m3u8") 
                        ? "application/x-mpegURL" 
                        : "video/mp4";

                    playerRef.current.src({ type: videoType, src: videoUrl });
                    
                    if (captions) {
                        playerRef.current.addRemoteTextTrack({
                            src: captions,
                            label: "English",
                            kind: "captions",
                            srclang: "en-us",
                            default: true,
                        }, false);
                    }
                }
            } catch (error) {
                console.error('Error initializing video player:', error);
            }
        };

        // Delay initialization slightly to ensure DOM is ready
        setTimeout(initPlayer, 100);

        // Cleanup function
        return () => {
            if (playerRef.current) {
                playerRef.current.pause();
            }
        };
    }, [videoUrl, poster, captions]);

    // Only dispose on unmount
    useEffect(() => {
        return () => {
            if (playerRef.current) {
                playerRef.current.dispose();
                playerRef.current = null;
            }
        };
    }, []);

    return (
        <div className="video-player-container">
            <div className="video-container" data-vjs-player>
                <video ref={videoRef} className="video-js" playsInline></video>
            </div>
        </div>
    );
};

export default VideoPlayer;
